import { handleError, type CustomError } from '@/utilities';
import store from '@/store';

/**
 * Strips "www." from the beginning of a hostname
 *
 * @param hostname - The hostname to strip
 * @returns The hostname without "www." prefix
 */
const stripWww = (hostname: string): string => hostname.replace(/^www\./i, '');

/**
 * Determines if a domain is a subdomain of a parent domain
 *
 * @param domain - The domain to check
 * @param parentDomain - The potential parent domain
 * @returns True if domain is a subdomain of parentDomain, false otherwise
 */
const isSubdomain = (domain: string, parentDomain: string): boolean => {
  const strippedDomain = stripWww(domain);
  const strippedParentDomain = stripWww(parentDomain);
  return (
    strippedDomain !== strippedParentDomain &&
    strippedDomain.endsWith(`.${strippedParentDomain}`)
  );
};

/**
 * Determines if a link should have the _cord_id param added.
 *
 * - Not the same domain as current
 * - Is in domains list or is a subdomain of a domain in the list
 */
function shouldAddCordId(
  linkHostname: string,
  currentHostname: string,
  domains: string[],
): boolean {
  const strippedLink = stripWww(linkHostname);
  const strippedCurrent = stripWww(currentHostname);

  if (strippedLink === strippedCurrent) return false;

  // Exact match or subdomain
  if (domains.map(stripWww).includes(strippedLink)) return true;
  return domains.some((domain) => isSubdomain(linkHostname, domain));
}

export async function addQueryParams() {
  const crossSiteTracking =
    store.config?.regionTemplate?.settings?.crossSiteTracking;
  const domains = store.config?.domains ?? [];
  const contextId = store.app.getContextId();

  if (!crossSiteTracking || domains.length === 0 || !contextId) {
    return;
  }

  const currentHostname = window.location.hostname;

  try {
    const elements = Array.from(document.getElementsByTagName('a'));
    if (elements.length === 0) return;

    elements.forEach((element) => {
      const href = element.getAttribute('href');
      if (!href) {
        return;
      }

      // Skip non-HTTP links early
      if (
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:')
      ) {
        return;
      }

      let url: URL;
      try {
        // Try parsing without a base. If it succeeds, it's an absolute URL.
        url = new URL(href);
      } catch {
        // If parsing fails, it's a relative or invalid URL.
        // Relative URLs stay on the same domain, so we can skip them.
        return;
      }

      if (shouldAddCordId(url.hostname, currentHostname, domains)) {
        url.searchParams.set('_cord_id', contextId);
        element.setAttribute('href', url.href);
      }
    });
  } catch (err) {
    handleError(err as CustomError);
  }
}

function debounce(fn: () => void, delay: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(fn, delay);
  };
}

/**
 * Observes the DOM for added <a> tags and runs addQueryParams efficiently.
 * Debounced to avoid excessive calls.
 */
export function observeAndAddQueryParams() {
  const debouncedAdd = debounce(addQueryParams, 200);

  // Run once initially
  addQueryParams();

  function startObserver() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (
            node.nodeType === 1 &&
            ((node as Element).tagName === 'A' ||
              (node as Element).querySelector?.('a'))
          ) {
            debouncedAdd();
            return;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.body) {
    startObserver();
  } else {
    window.addEventListener('DOMContentLoaded', startObserver, { once: true });
  }
}
